import React, { useState, useCallback } from 'react';
import { Category, CategoryType } from '../../types/budget';
import { useBudget } from './BudgetContext';
import { Button } from '../ui/Button';
import styles from './CategoryManager.module.css';

interface CategoryManagerProps {
  onClose: () => void;
}

const PRESET_COLORS = [
  '#4A90D9', '#7B68EE', '#FF6B6B', '#4ECDC4', '#45B7D1',
  '#96CEB4', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE',
  '#85C1E9', '#F8B500', '#48C9B0', '#AF7AC5', '#5DADE2',
  '#F1948A', '#000000', '#E74C3C', '#8B949E', '#3FB950',
];

export const CategoryManager: React.FC<CategoryManagerProps> = ({
  onClose,
}) => {
  const {
    categories,
    transactions,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useBudget();

  const getTransactionCount = useCallback((categoryId: string) => {
    return transactions.filter(t => t.categoryId === categoryId).length;
  }, [transactions]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState<CategoryType>('expense');
  const [newCategoryColor, setNewCategoryColor] = useState('#58A6FF');
  const [isCreating, setIsCreating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const expenseCategories = categories.filter(c => c.type === 'expense').sort((a, b) => a.sortOrder - b.sortOrder);
  const incomeCategories = categories.filter(c => c.type === 'income').sort((a, b) => a.sortOrder - b.sortOrder);
  const transferCategories = categories.filter(c => c.type === 'transfer').sort((a, b) => a.sortOrder - b.sortOrder);

  const handleEditStart = (category: Category) => {
    setEditingId(category.id);
    setEditName(category.name);
    setEditColor(category.color);
  };

  const handleEditSave = async () => {
    if (!editingId || !editName.trim()) return;

    setIsProcessing(true);
    try {
      await updateCategory(editingId, {
        name: editName.trim(),
        color: editColor,
      });
      setEditingId(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
  };

  const handleDelete = async (id: string, name: string) => {
    const count = getTransactionCount(id);
    const message = count > 0
      ? `"${name}" has ${count} transaction${count !== 1 ? 's' : ''}. They will be marked as uncategorized. Delete anyway?`
      : `Delete category "${name}"?`;

    if (window.confirm(message)) {
      setIsProcessing(true);
      try {
        await deleteCategory(id);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCreate = async () => {
    if (!newCategoryName.trim()) return;

    setIsProcessing(true);
    try {
      await createCategory({
        name: newCategoryName.trim(),
        type: newCategoryType,
        color: newCategoryColor,
      });
      setNewCategoryName('');
      setIsCreating(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderCategoryRow = (category: Category) => {
    const isEditing = editingId === category.id;
    const count = getTransactionCount(category.id);

    if (isEditing) {
      return (
        <div key={category.id} className={styles.categoryRow}>
          <input
            type="text"
            className={styles.editInput}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            autoFocus
          />
          <div className={styles.colorPicker}>
            <span
              className={styles.colorPreview}
              style={{ backgroundColor: editColor }}
            />
            <select
              className={styles.colorSelect}
              value={editColor}
              onChange={(e) => setEditColor(e.target.value)}
            >
              {PRESET_COLORS.map((color) => (
                <option key={color} value={color}>
                  {color}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.rowActions}>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEditCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleEditSave}
              disabled={isProcessing || !editName.trim()}
              loading={isProcessing}
            >
              Save
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div key={category.id} className={styles.categoryRow}>
        <span
          className={styles.colorIndicator}
          style={{ backgroundColor: category.color }}
        />
        <span className={styles.categoryName}>{category.name}</span>
        <span className={styles.transactionCount}>
          {count} transaction{count !== 1 ? 's' : ''}
        </span>
        <div className={styles.rowActions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditStart(category)}
            disabled={isProcessing}
            aria-label={`Edit ${category.name}`}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(category.id, category.name)}
            disabled={isProcessing || category.name === 'Uncategorized'}
            aria-label={`Delete ${category.name}`}
          >
            Delete
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Manage Categories</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </Button>
        </div>

        <div className={styles.content}>
          {/* Expense Categories */}
          <div className={styles.section}>
            <h3>Expense Categories</h3>
            <div className={styles.categoryList}>
              {expenseCategories.map(renderCategoryRow)}
            </div>
          </div>

          {/* Income Categories */}
          <div className={styles.section}>
            <h3>Income Categories</h3>
            <div className={styles.categoryList}>
              {incomeCategories.map(renderCategoryRow)}
            </div>
          </div>

          {/* Transfer Categories */}
          <div className={styles.section}>
            <h3>Transfer Categories</h3>
            <div className={styles.categoryList}>
              {transferCategories.map(renderCategoryRow)}
            </div>
          </div>

          {/* Add New Category */}
          {isCreating ? (
            <div className={styles.createForm}>
              <div className={styles.createRow}>
                <input
                  type="text"
                  className={styles.editInput}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name"
                  autoFocus
                />
                <select
                  className={styles.typeSelect}
                  value={newCategoryType}
                  onChange={(e) => setNewCategoryType(e.target.value as CategoryType)}
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                  <option value="transfer">Transfer</option>
                </select>
                <div className={styles.colorPicker}>
                  <span
                    className={styles.colorPreview}
                    style={{ backgroundColor: newCategoryColor }}
                  />
                  <select
                    className={styles.colorSelect}
                    value={newCategoryColor}
                    onChange={(e) => setNewCategoryColor(e.target.value)}
                  >
                    {PRESET_COLORS.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.createActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsCreating(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreate}
                  disabled={isProcessing || !newCategoryName.trim()}
                  loading={isProcessing}
                >
                  Add Category
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCreating(true)}
              fullWidth
              leftIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              }
            >
              Add New Category
            </Button>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
